import React, { memo, useState, useCallback } from 'react'
import { makeStyles, withStyles } from '@material-ui/styles'
import { grey } from '@material-ui/core/colors'
import ButtonBase from '@material-ui/core/ButtonBase'
import classNames from 'classnames'
import ArrowRight from '@material-ui/icons/ChevronRightRounded'
import { useApolloClient, useMutation } from '@apollo/react-hooks'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useTranslation, Trans } from 'react-i18next'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox, { CheckboxProps } from '@material-ui/core/Checkbox'

import { Textfield, LinkedinLogin, GoogleLogin, FacebookLogin } from '../'
import { ModalTermsAndConditions } from 'components'
import { AuthStore } from '../../reducer'
import styles from './styles'
import { CheckEmailQuery, RegisterMutation } from 'store'
import {
	TSignUpProps,
	EAuthTextfieldType,
	EAuthActionTypes,
	EAuthSectionTypes,
} from 'models'
import mixpanel from 'mixpanel-browser'

const useStyles = makeStyles(styles)

const WhiteCheckbox = withStyles({
	root: {
		color: grey[50],
		'&$checked': {
			color: grey[50],
		},
	},
	checked: {},
})((props: CheckboxProps) => <Checkbox color="default" {...props} />)

const LoginView: React.FunctionComponent<TSignUpProps> = props => {
	const {
		step,
		validation,
		values: { id, email, password, firstName, lastName },
		dispatch,
	} = props
	const { t, i18n } = useTranslation()
	const classes = useStyles()

	const [loading, toggle] = useState(false)
	const [termsAndConditionsModal, setTermsAndConditionsModal] = useState(
		false,
	)
	const [termsAndConditionsCheckbox, setTermsAndConditionsCheckox] = useState(
		false,
	)
	const goToLogin = () => {
		dispatch({
			type: EAuthActionTypes.setSection,
			section: EAuthSectionTypes.login,
		})
	}
	const client = useApolloClient()
	const [registerMutationHandle] = useMutation(RegisterMutation, {
		variables: {
			data: {
				firstName,
				lastName,
				email,
				password,
				language: i18n.language,
			},
		},
	})
	const nextStep = async () => {
		toggle(true)
		switch (step) {
			case 0:
				const { data } = await client.query({
					query: CheckEmailQuery,
					variables: {
						email,
					},
				})
				if (data && data.checkEmail && data.checkEmail.id) {
					dispatch({
						type: EAuthActionTypes.setTemporaryUser,
						user: data.checkEmail,
					})
					goToLogin()
				} else {
					dispatch({
						type: EAuthActionTypes.incrementStep,
					})
					toggle(false)
				}
				break
			case 1:
				// response =
				await registerMutationHandle()
				dispatch({
					type: EAuthActionTypes.setSignUpSuccess,
				})
				mixpanel.identify(id)
				mixpanel.people.set({
					$first_name: firstName,
					$last_name: lastName,
					$email: email,
				})
				break
			default:
				break
		}
	}
	const toggleTermsAndConditionsModal = useCallback(
		event => {
			event.preventDefault()
			setTermsAndConditionsModal(!termsAndConditionsModal)
		},
		[termsAndConditionsModal],
	)
	const toggleTermsAndConditionsCheckbox = useCallback(() => {
		setTermsAndConditionsCheckox(!termsAndConditionsCheckbox)
	}, [termsAndConditionsCheckbox])

	const handleAgreement = useCallback(
		(agreement: boolean) => {
			setTermsAndConditionsModal(false)
			setTermsAndConditionsCheckox(agreement)
		},
		[setTermsAndConditionsModal, setTermsAndConditionsCheckox],
	)

	return (
		<>
			<div className={classes.container}>
				<Textfield
					type={EAuthTextfieldType.email}
					disabled={step === 1}
				/>
				{step === 1 && (
					<>
						<div className={classes.nameContainer}>
							<Textfield
								type={EAuthTextfieldType.firstName}
								placeholder={t('John')}
							/>
							<Textfield
								type={EAuthTextfieldType.lastName}
								placeholder={t('Smith')}
							/>
						</div>
						<Textfield type={EAuthTextfieldType.password} />
					</>
				)}
				{/* 
			<ButtonBase className={classes.forgotPasswordLink}>
				Forgot your password?
			</ButtonBase> */}
				<div className={classes.errorMessagesContainer}>
					{/*capslock && <div className={classes.capslockWarning}>
					<Capslock />
					Caps Lock is on
				</div>*/}
				</div>
				{!loading && step === 1 && (
					<>
						<FormControlLabel
							control={
								<WhiteCheckbox
									inputProps={{
										id: 'termsAndConditionCheckbox',
									}}
									onChange={toggleTermsAndConditionsCheckbox}
									checked={termsAndConditionsCheckbox}
									value=""
									color="primary"
								/>
							}
							className={classNames(
								classes.termsAndConditionsLabel,
							)}
							label={
								<Trans i18nKey="">
									<span
										onClick={event =>
											event.preventDefault()
										}
									>
										{t('I read and I agree to the')}
									</span>{' '}
									<u
										onClick={event =>
											toggleTermsAndConditionsModal(event)
										}
										className={classNames(
											classes.termsAndConditionsLink,
										)}
									>
										{t('Terms and conditions')}
									</u>
								</Trans>
							}
						/>
						<ModalTermsAndConditions
							handleAgreement={handleAgreement}
							termsAndConditionsModal={termsAndConditionsModal}
						/>
					</>
				)}
				<ButtonBase
					onClick={nextStep}
					className={classNames(classes.loginButton, {
						[classes.loginButtonActive]:
							(step === 0 && validation.email) ||
							(step === 1 &&
								validation.password &&
								validation.firstName &&
								termsAndConditionsCheckbox &&
								validation.lastName),
					})}
					disabled={
						(step === 0 && !validation.email) ||
						(step === 1 &&
							(!validation.password ||
								!validation.firstName ||
								!validation.lastName ||
								!termsAndConditionsCheckbox ||
								loading))
					}
				>
					{!loading && (
						<>
							{step === 0 ? 'Next' : 'Create Account'}
							{step === 0 && <ArrowRight />}
						</>
					)}
					{loading && (
						<CircularProgress
							className={classes.loading}
							size={20}
						/>
					)}
				</ButtonBase>
				{step === 0 && (
					<>
						<div className={classes.authDelimiter}>
							<hr />
							<span>OR</span>
							<hr />
						</div>
						<div className={classes.socialLinks}>
							<LinkedinLogin loading={loading} toggle={toggle} />
							<GoogleLogin loading={loading} toggle={toggle} />
							<FacebookLogin loading={loading} toggle={toggle} />
						</div>
					</>
				)}
			</div>
			<div className={classes.createAccount}>
				<span>{t('You already have an account?')}</span>
				<ButtonBase onClick={goToLogin}>{t('Log in now!')}</ButtonBase>
			</div>
		</>
	)
}

export default memo((props: any) => (
	<AuthStore keys={['step', 'validation', 'values', 'dispatch']}>
		<LoginView {...props} />
	</AuthStore>
))
